import {Box, HStack, Text} from "@chakra-ui/react";
import React, {Dispatch, forwardRef, ReactNode, SetStateAction, useCallback, useRef, useState} from "react";
import {FilterEntries, FilterEntry, FilterState, isFilterValueEmpty} from "../../../../hooks/filter/filterState";
import {CombinedFilterContext} from "../../../../hooks/filter/filterConfig";
import {AnyFilterDefinitions} from "../../../../hooks/filter/filterConfig";
import {FilterKey, generateFilterKey} from "./filterKey";
import {typedMemo} from "../../../../utils/typescript";
import {AddFilterMenu} from "./AddFilterMenu";
import {ClearFiltersButton} from "./ClearFiltersButton";
import {AnimatePresence, motion} from "framer-motion";
import {MatchFiltersButton} from "./MatchFiltersButton";
import {FilterChip} from "./FilterChip";
import {useSubState} from "../../../../hooks/subState";
import useProjectedValue from "../../../../hooks/projectedValue";
import {FacetCount} from "platformed-client/types";
import T from "platformed-components/i18n/T";

/**
 * The gap between items in the filter bar. This is pulled out as a constant
 * because it's also used by the invisible menu anchor to negate the gap it
 * creates when it exists.
 */
export const FILTER_BAR_GAP = 2;

/**
 * The menu anchor is an invisible element that determines where the "Add filter"
 * menu is positioned.
 *
 * It is always placed just after the last filter and before any new filters created
 * by user interaction with the menu. It serves as a stable anchor point for the menu
 * that does not change as the filter being created changes size.
 *
 * Using an explicit element instead of setting an anchor offset for the menu is
 * necessary because the filter bar can wrap to multiple rows. The easiest way
 * to position the menu correctly is to just let the browser's flexbox algorithm
 * position an invisible anchor element.
 */
export const MenuAnchor = forwardRef((_, ref) => {
  return <Box ref={ref} alignSelf="stretch" mr={-FILTER_BAR_GAP} pointerEvents="none" />;
});

/**
 * Animate the position of its children. This wraps several of the components on
 * the filter bar to animate them nicely as filters are added, removed, or
 * change size.
 */
export const AnimatePosition = ({children, ...props}: {children: ReactNode}) => (
  <motion.div
    layout="position"
    transition={{
      duration: 0.1,
      ease: [0.25, 1, 0.5, 1], // quartOut
    }}
    {...props}
  >
    {children}
  </motion.div>
);

export type VisibleFilterEntry<Ds extends AnyFilterDefinitions> = {key: FilterKey; entry: FilterEntry<Ds, keyof Ds>};

const FilterEditor = typedMemo(
  <Ds extends AnyFilterDefinitions>({
    filters,
    state,
    setState,
    hiddenFilters,
    facetCounts,
    context,
  }: {
    filters: Ds;
    state: FilterState<Ds>;
    setState: Dispatch<SetStateAction<FilterState<Ds>>>;
    /** Do not show these filters in the "add filter" menu. */
    hiddenFilters?: (keyof Ds)[];
    facetCounts: {
      [K in keyof Ds]?: FacetCount[];
    };
    context: CombinedFilterContext<Ds>;
  }) => {
    const [menuOpen, setMenuOpen] = useState(false);

    const [entries, setEntries] = useSubState(state, setState, l => l.property("entries"));
    const [operator, setOperator] = useSubState(state, setState, l => l.property("operator"));

    const projectEntries = useCallback(
      (entries: FilterEntries<Ds>) => entries.map(entry => ({entry, key: generateFilterKey()})),
      [],
    );
    const unprojectEntries = useCallback(
      (projected: VisibleFilterEntry<Ds>[]) =>
        projected.map(p => p.entry).filter(p => !isFilterValueEmpty(filters[p.kind], p.filter)),
      [filters],
    );

    const [visibleEntries, setVisibleEntries] = useProjectedValue(
      entries,
      setEntries,
      projectEntries,
      unprojectEntries,
    );

    // After which chip should the menu anchor be placed?
    // - If `-1`, the anchor is shown at the start of the bar
    // - If `n`, the anchor is shown after the nth chip.
    const [menuAnchorIndex, setMenuAnchorIndex] = useState<number>(-1);

    const onMenuOpenChange = useCallback(
      (open: boolean) => {
        setMenuOpen(open);

        if (open) {
          // When the menu is opened, store the position of the anchor. If there
          // are no entries this gets set to `-1` which means the menu is placed
          // at the start of the bar, before any chips.
          setMenuAnchorIndex(visibleEntries.length - 1);
        }
      },
      [visibleEntries.length],
    );

    const menuAnchorRef = useRef<HTMLDivElement>(null);

    // A table with nothing the menu would list gets no trigger.
    const hasDefinitions = Object.keys(filters).some(kind => hiddenFilters == null || !hiddenFilters.includes(kind));

    return (
      <HStack flex="1" fontSize="sm">
        <AnimatePresence>
          <HStack gap={FILTER_BAR_GAP} wrap="wrap">
            {menuAnchorIndex === -1 && <MenuAnchor ref={menuAnchorRef} />}
            {visibleEntries.map(({key, entry}, idx) => (
              <React.Fragment key={key}>
                {idx !== 0 && (
                  <Text color="fg.muted" fontSize="2xs">
                    {operator === "all" ? <T keyName="c.and" /> : <T keyName="c.or" />}
                  </Text>
                )}
                <AnimatePosition>
                  <FilterChip
                    definition={filters[entry.kind]}
                    filter={entry.filter}
                    onApply={action => {
                      const newValue = action instanceof Function ? action(entry.filter) : entry.filter;
                      setVisibleEntries(prev =>
                        prev.map(ve => (ve.key === key ? {...ve, entry: {...ve.entry, filter: newValue}} : ve)),
                      );
                    }}
                    onRemove={() => {
                      setVisibleEntries(prev => prev.filter(ve => ve.key !== key));
                    }}
                    onOpenChange={open => {
                      if (!open) {
                        setVisibleEntries(prev =>
                          prev.filter(ve => !isFilterValueEmpty(filters[ve.entry.kind], ve.entry.filter)),
                        );
                      }
                    }}
                    facet={facetCounts[entry.kind] ?? []}
                    context={context as object}
                  />
                </AnimatePosition>
                {idx === menuAnchorIndex && <MenuAnchor ref={menuAnchorRef} />}
              </React.Fragment>
            ))}
            {hasDefinitions && (
              <AnimatePosition>
                <AddFilterMenu
                  definitions={filters}
                  menuAnchorRef={menuAnchorRef}
                  open={menuOpen}
                  onOpenChange={onMenuOpenChange}
                  hiddenFilters={hiddenFilters}
                  visibleEntries={visibleEntries}
                  setVisibleEntries={setVisibleEntries}
                  facetCounts={facetCounts}
                  context={context as object}
                  onExitComplete={() => setMenuAnchorIndex(-1)}
                />
              </AnimatePosition>
            )}
          </HStack>
        </AnimatePresence>
        <HStack ml="auto">
          {entries.length > 1 && <MatchFiltersButton operator={operator} onChangeOperator={setOperator} />}
          {entries.length > 0 && <ClearFiltersButton onClick={() => setState({entries: [], operator: "all"})} />}
        </HStack>
      </HStack>
    );
  },
);

export default FilterEditor;
